/**
 * Shared fiscal year utilities.
 *
 * UniqueX fiscal year runs July-June:
 *   Q1 = Jul-Sep (index 0)
 *   Q2 = Oct-Dec (index 1)
 *   Q3 = Jan-Mar (index 2)
 *   Q4 = Apr-Jun (index 3)
 */

/** Calendar months in fiscal year order (Jul=7 through Jun=6) */
export const FISCAL_MONTH_ORDER = [7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6];

/** Calendar months per quarter index */
export const QUARTER_MONTHS: Record<number, number[]> = {
  0: [7, 8, 9],   // Q1: Jul-Sep
  1: [10, 11, 12], // Q2: Oct-Dec
  2: [1, 2, 3],   // Q3: Jan-Mar
  3: [4, 5, 6],   // Q4: Apr-Jun
};

/** Current fiscal quarter index (0-3) */
export const getCurrentQuarterIndex = (): number => {
  const month = new Date().getMonth(); // 0-based (Jan=0)
  if (month >= 6 && month <= 8) return 0; // Q1: Jul-Sep
  if (month >= 9 && month <= 11) return 1; // Q2: Oct-Dec
  if (month >= 0 && month <= 2) return 2; // Q3: Jan-Mar
  return 3; // Q4: Apr-Jun
};

/** Quarter status relative to current quarter */
export const getQuarterStatus = (
  quarterIndex: number,
  currentQuarterIndex: number
): 'past' | 'current' | 'future' => {
  if (quarterIndex < currentQuarterIndex) return 'past';
  if (quarterIndex === currentQuarterIndex) return 'current';
  return 'future';
};

/** Current fiscal month index (0-11, where Jul=0, Jun=11) */
export const getCurrentFiscalMonthIndex = (): number => {
  const calMonth = new Date().getMonth() + 1; // 1-based
  return FISCAL_MONTH_ORDER.indexOf(calMonth);
};

/** Month status (past/current/future) based on calendar month (1-12) */
export const getMonthStatus = (
  calendarMonth: number
): 'past' | 'current' | 'future' => {
  const currentCal = new Date().getMonth() + 1;
  const currentFiscal = FISCAL_MONTH_ORDER.indexOf(currentCal);
  const targetFiscal = FISCAL_MONTH_ORDER.indexOf(calendarMonth);
  if (targetFiscal < currentFiscal) return 'past';
  if (targetFiscal === currentFiscal) return 'current';
  return 'future';
};

/** Get the 3 calendar months for a quarter index (0-3) */
export const getMonthsForQuarter = (quarterIndex: number): number[] => {
  return QUARTER_MONTHS[quarterIndex] || [];
};

/** Get the quarter index (0-3) for a calendar month (1-12) */
export const getQuarterForMonth = (calendarMonth: number): number => {
  for (const [qIdx, months] of Object.entries(QUARTER_MONTHS)) {
    if (months.includes(calendarMonth)) return parseInt(qIdx);
  }
  return -1;
};

/** Short month label from calendar month (1-12) */
export const getMonthLabel = (calendarMonth: number): string => {
  const labels: Record<number, string> = {
    1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun',
    7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec',
  };
  return labels[calendarMonth] || '';
};

/** Get the fiscal year for a given date. FY starts in July. */
export const getFiscalYear = (date: Date = new Date()): number => {
  const month = date.getMonth(); // 0-based
  const year = date.getFullYear();
  // If Jul-Dec, fiscal year is next calendar year (e.g., Jul 2025 = FY2026)
  return month >= 6 ? year + 1 : year;
};
